import { useState } from "react";
import { Trash2 } from "lucide-react";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@b2b/ui-components";
import type { ProfileData } from "../types/profile";
import { DragDropZone } from "./BrandingMediaSection";
import { TOTAL_FACTORY_SIZE_OPTIONS } from "../../../constants/factorySizeOptions";

const MAX_IMAGE_FILES = 5;
const MAX_IMAGE_MB = 2;
const ACCEPT_IMAGE = "image/jpeg,image/jpg,image/png";

function infrastructureImageLabel(url: string, index: number): string {
  try {
    const path = url.split("?")[0] || url;
    const seg = path.split("/").filter(Boolean).pop();
    if (seg) return seg;
  } catch {
    /* ignore */
  }
  return `Image ${index + 1}`;
}

function InfrastructureImageThumbnail({
  url,
  imageIndex,
  isEditing,
  onRemove,
}: {
  url: string;
  imageIndex: number;
  isEditing: boolean;
  onRemove: () => void;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const label = infrastructureImageLabel(url, imageIndex);

  return (
    <div className="relative flex w-[132px] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        title={label}
      >
        {imageFailed ? (
          <div className="flex h-[104px] w-full flex-col items-center justify-center gap-1 bg-gray-100 px-2 text-center">
            <span className="line-clamp-2 text-[10px] leading-tight text-gray-600">Preview unavailable</span>
          </div>
        ) : (
          <img
            src={url}
            alt={label}
            className="h-[104px] w-full bg-gray-50 object-cover"
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        )}
      </a>
      <div className="flex min-h-[32px] items-center justify-between gap-1 border-t border-gray-100 px-1.5 py-1">
        <span className="flex-1 truncate text-[10px] text-gray-600" title={url}>
          {label}
        </span>
        {isEditing && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemove();
            }}
            className="shrink-0 rounded p-0.5 text-destructive hover:text-destructive/80"
            aria-label="Remove infrastructure image"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function getStr(f: { value?: unknown } | undefined): string {
  if (f?.value == null) return "";
  return typeof f.value === "string" ? f.value : String(f.value);
}

function getArr(f: { value?: unknown } | undefined): string[] {
  if (!f?.value || !Array.isArray(f.value)) return [];
  return f.value.map((v) => (typeof v === "string" ? v : String(v)));
}

interface FactoryWarehouseSectionProps {
  formData: ProfileData;
  isEditing: boolean;
  onFieldChange: (fieldKey: string, value: string | File | boolean | string[]) => void;
  errors?: Record<string, string>;
  /** Upload a file to storage and return the public URL (same as Company Brochures). */
  onUpload?: (file: File) => Promise<string>;
}

export function FactoryWarehouseSection({
  formData,
  isEditing,
  onFieldChange,
  onUpload,
}: FactoryWarehouseSectionProps) {
  const ci = formData.companyInfo || {};
  const infrastructureImages = getArr(ci.infrastructureImages);
  const [infrastructureUploading, setInfrastructureUploading] = useState(false);
  const [infrastructureError, setInfrastructureError] = useState<string | null>(null);

  const maxBytes = MAX_IMAGE_MB * 1024 * 1024;

  const handleInfrastructureFiles = async (files: File | File[]) => {
    const fileArr = Array.isArray(files) ? files : [files];
    setInfrastructureError(null);
    const remaining = MAX_IMAGE_FILES - infrastructureImages.length;
    const toTake = fileArr.slice(0, remaining);
    if (toTake.length === 0) {
      setInfrastructureError(`Maximum ${MAX_IMAGE_FILES} images allowed.`);
      return;
    }
    const valid = toTake.filter((f) => f.size <= maxBytes);
    if (valid.length < toTake.length) {
      setInfrastructureError(`Some files exceed ${MAX_IMAGE_MB}MB. Only files up to ${MAX_IMAGE_MB}MB are allowed.`);
    }
    if (valid.length === 0) return;

    if (onUpload) {
      setInfrastructureUploading(true);
      try {
        const urls = await Promise.all(valid.map((f) => onUpload(f)));
        onFieldChange("infrastructureImages", [...infrastructureImages, ...urls]);
      } catch {
        setInfrastructureError("Upload failed. Please try again.");
      } finally {
        setInfrastructureUploading(false);
      }
    } else {
      const urls = valid.map((f) => URL.createObjectURL(f));
      onFieldChange("infrastructureImages", [...infrastructureImages, ...urls].slice(0, MAX_IMAGE_FILES));
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-gray-700">Total Factory Size</Label>
        <Select
          value={getStr(ci.totalFactorySize)}
          onValueChange={(v) => onFieldChange("totalFactorySize", v)}
          disabled={!isEditing}
        >
          <SelectTrigger className="w-full max-w-md h-10 border-gray-300 rounded bg-white">
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent className="bg-white border border-gray-300 shadow-md z-50">
            {TOTAL_FACTORY_SIZE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700">Infrastructure Image</Label>
        {infrastructureImages.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-3">
            {infrastructureImages.map((url, idx) => (
              <InfrastructureImageThumbnail
                key={`infra-${idx}-${url.slice(-24)}`}
                url={url}
                imageIndex={idx}
                isEditing={isEditing}
                onRemove={() =>
                  onFieldChange(
                    "infrastructureImages",
                    infrastructureImages.filter((_, i) => i !== idx),
                  )
                }
              />
            ))}
          </div>
        )}
        {isEditing && infrastructureImages.length < MAX_IMAGE_FILES && (
          <DragDropZone
            hint={`Drag & drop or choose files (JPEG, JPG, PNG), up to ${MAX_IMAGE_FILES} files`}
            accept={ACCEPT_IMAGE}
            maxSizeMB={MAX_IMAGE_MB}
            value={[]}
            multiple
            onFile={handleInfrastructureFiles}
            isEditing={isEditing}
            uploading={infrastructureUploading}
            uploadError={infrastructureError}
          />
        )}
        {!isEditing && infrastructureImages.length === 0 && (
          <p className="text-sm text-muted-foreground">No infrastructure images uploaded</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-gray-700">No. of staff</Label>
        <Input
          type="number"
          min={0}
          step={1}
          inputMode="numeric"
          autoComplete="off"
          value={(() => {
            const s = getStr(ci.qcStaffCount).trim();
            if (s === "") return "";
            const n = parseInt(s, 10);
            if (Number.isNaN(n)) return "";
            return String(Math.max(0, n));
          })()}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "") {
              onFieldChange("qcStaffCount", "");
              return;
            }
            const n = Number(v);
            if (Number.isNaN(n)) return;
            onFieldChange("qcStaffCount", String(Math.max(0, Math.floor(n))));
          }}
          onKeyDown={(e) => {
            if (e.ctrlKey || e.metaKey || e.altKey) return;
            if (["-", "+", "e", "E", "."].includes(e.key)) {
              e.preventDefault();
            }
          }}
          disabled={!isEditing}
          className="max-w-md h-10 border-gray-300 rounded"
        />
      </div>
    </div>
  );
}
